import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // 1. Estados: mode (preferencia del usuario) y theme (lo que se aplica realmente)
    const [mode, setMode] = useState(() => {
        return localStorage.getItem('theme-mode') || 'system';
    });

    const [theme, setTheme] = useState('light');

    // 2. Lógica para determinar el tema según el modo y el sistema
    const getSystemTheme = useCallback(() => {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }, []);

    const updateTheme = useCallback(() => {
        if (mode === 'system') {
            setTheme(getSystemTheme());
        } else {
            setTheme(mode);
        }
    }, [mode, getSystemTheme]);

    // 3. Efecto para reaccionar a cambios de modo o cambios en el sistema (si modo es system)
    useEffect(() => {
        updateTheme();
        localStorage.setItem('theme-mode', mode);

        if (mode === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => setTheme(getSystemTheme());

            // Compatibilidad para navegadores antiguos
            if (mediaQuery.addEventListener) {
                mediaQuery.addEventListener('change', handleChange);
            } else {
                mediaQuery.addListener(handleChange);
            }

            return () => {
                if (mediaQuery.removeEventListener) {
                    mediaQuery.removeEventListener('change', handleChange);
                } else {
                    mediaQuery.removeListener(handleChange);
                }
            };
        }
    }, [mode, updateTheme, getSystemTheme]);

    // 4. Efecto para aplicar el atributo al HTML
    useEffect(() => {
        const root = window.document.documentElement;
        root.setAttribute('data-theme', theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{
            theme,
            mode,
            setMode,
            isDarkMode: theme === 'dark',
            toggleTheme: () => setMode(prev => prev === 'dark' ? 'light' : 'dark')
        }}>
            {children}
        </ThemeContext.Provider>
    );
};
